<template>
  <div id="files-information">
    <div class="d-flex align-items-center justify-content-between mb-5">
      <h4 class="mb-0">{{ title }}</h4>
      <va-popover
          class="clickable"
          v-if="editable && hasWriteAppointmentsPermissions"
          placement="left"
          :message="$t('homelab.files.upload_files')">
        <span @click.prevent="triggerFileUpload">
          <i class="glyphicon glyphicon-plus px-3 mr-3"></i>
        </span>
      </va-popover>
    </div>

    <div class="row">
      <v-client-table
        id="filesTable"
        ref="filesTable"
        :columns="tableColumns"
        :data="tableData"
        :options="filesTableOptions"
        class="col-12"
      >
        <template slot="name" slot-scope="props">
          {{ props.row.name.split('.')[0]}}
        </template>

        <template slot="actions" slot-scope="props">
          <va-popover
            placement="left"
            :message="$t('homelab.files.download_file')"
            class="pl-2">
            <span>
              <i class="fa fa-download" @click.prevent.stop="downloadFile(props.row)" />
            </span>
          </va-popover>
          &nbsp;
          <va-popover
            placement="left"
            :message="$t('homelab.files.delete_file')"
            v-if="editable && hasWriteAppointmentsPermissions">
            <span>
              <i class="fa fa-times" @click.prevent.stop="triggerFileDeletion(props.row)"/>
            </span>
          </va-popover>
          &nbsp;
          <va-popover
            placement="left"
            :message="$t('homelab.files.update_file')"
            v-if="editable && hasWriteAppointmentsPermissions">
            <span>
              <i class="fa fa-pencil" @click.prevent.stop="triggerFileUpdate(props.row)"/>
            </span>
          </va-popover>
        </template>

      </v-client-table>

      <UploadFilesModal
        v-if="isUpdatingFile"
        :appointment="appointment"
        :fileType="fileType"
        @file-uploaded="onFileUploaded"
        @cancel="onModalUploadFilesClosed"
        :file="selectedFileToUpdate"
      >
      </UploadFilesModal>

      <ConfirmationModal
        v-if="isDeletingFile"
        :title="$t('homelab.files.delete_file')"
        :body="$t('homelab.files.delete_file_confirm', {fileName:this.selectedFileToDelete.name.split('.')[0]})"
        @modal-canceled="cancelFileDeletion"
        @modal-confirmed="confirmFileDeletion"
      />
    </div>
  </div>
</template>
<script>
import Vue from 'vue'
import { AppointmentFilesService } from '@/api/HomelabService'
import UploadFilesModal from '@/components/homelab/appointments/UploadFilesModal'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { ClientTable } from 'vue-tables-2'
import {mapState} from "vuex";
Vue.use(ClientTable, {}, false, 'bootstrap4')

export default {
  components: {
    UploadFilesModal,
    ConfirmationModal
  },
  props: {
    title: {
      type: String,
      required: true
    },
    fileType: {
      type: Number,
      required: true
    },
    appointment: {
      type: Object,
      required: true
    },
    files: {
      type: Array,
      required: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      selectedFileToUpdate: null,
      isUpdatingFile: false,
      isDeletingFile: false,
      tableColumns: ['updated_at', 'name', 'original_extension', 'actions'],
      filesTableOptions: {
        texts: {
          count: this.$t('tables.records') + ' {from} ' + this.$t('tables.to') + ' {to} ' + this.$t('tables.of') + ' {count}|{count} ' + this.$t('tables.records_') + '|' + this.$t('tables.one_record'),
          first: this.$t('tables.first'),
          last: this.$t('tables.last'),
          filter: this.$t('tables.filter') + ':',
          filterPlaceholder: this.$t('tables.search_text'),
          limit: this.$t('tables.records') + ':',
          page: this.$t('tables.page') + ':',
          noResults: this.$t('tables.no_records'),
          filterBy: this.$t('tables.filter_by') + ' {column}',
          loading: this.$t('tables.loading') + '...',
          defaultOption: this.$t('tables.select') + ' {column}',
          columns: this.$t('tables.columns_dropdown')
        },
        sortable: [],
        filterable: false,
        headings: {
          'name': this.$t('homelab.labels.files_table.name'),
          'original_extension': this.$t('homelab.labels.files_table.type'),
          'updated_at': this.$t('homelab.labels.files_table.updated_at'),
          'actions': this.$t('homelab.labels.files_table.actions'),
        },
        columnsClasses: {
          name: 'name',
          original_extension: 'original_extension',
          updated_at: 'updated_at',
          actions: 'actions'
        },
        pagination: {
          chunk: 6,
          nav: 'scroll'
        },
      }
    }
  },
  computed: {
    ...mapState('homelab', [
      'appointmentFileTypes'
    ]),
    homelabService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.homelab_server)
    },
    hasWriteAppointmentsPermissions: function () {
      return this.homelabService.user_permissions.some(permission => (permission.entity_id === null || permission.entity_id === this.appointment.entity.id) && permission.access_type >= 2 && permission.info_type === 100)
    },
    tableData: function () {
      if (Array.isArray(this.files)) {
        return this.files
      } else if (this.appointment && Array.isArray(this.appointment.files)) {
        return this.appointment.files
      } else {
        return []
      }
    }
  },
  methods: {
    triggerFileDeletion: function (file) {
      this.selectedFileToDelete = file
      this.isDeletingFile = true
    },
    cancelFileDeletion: function () {
      this.selectedFileToDelete = null
      this.isDeleting = false
    },
    confirmFileDeletion: function () {
      AppointmentFilesService.delete(this.appointment.id, this.selectedFileToDelete.id)
        .then(response => {
          this.showToast(this.$t('homelab.files.deleted'), this.$store.state.successToastOptions)
          this.$emit('appointment-files-updated', { files: [...this.appointment.files].filter(file => file.id !== this.selectedFileToDelete.id) })
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('homelab.files.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.isDeletingFile = false
        })
    },
    triggerFileUpload: function () {
      this.selectedFileToUpdate = null
      this.isUpdatingFile = true
    },
    triggerFileUpdate: function (file) {
      this.selectedFileToUpdate = file
      this.isUpdatingFile = true
    },
    handleUpdateFile: function (file) {
      this.selectedFileToUpdate = file
      this.isUpdate = true
      this.isUpdatingFile = true
    },
    handleUploadModal: function () {
      this.isUpdate = false
      this.isUpdatingFile = true
    },
    onFileUploaded: function ({ file }) {
      let files = [...this.appointment.files]
      if (this.selectedFileToUpdate) {
        const index = files.findIndex(file => file.id === this.selectedFileToUpdate.id)
        if (index !== -1) {
          files[index] = file
        }
      } else {
        files.push(file)
      }
      this.$emit('appointment-files-updated', { files })
    },
    onModalUploadFilesClosed: function () {
      this.selectedFileToUpdate = null
      this.isUpdatingFile = false
    },
    downloadFile: function (row) {
      AppointmentFilesService.download(this.appointment.id, row.id)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response], { type: row.mime_type }))
          window.open(url, '_blank')
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('homelab.files.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    }
  }
}
</script>

<style lang="scss">

  #files-information {

    .table-bordered th, .table-bordered td {
      border: 0 !important;
    }

    .actions {
      text-align: center;
    }

    .actions {
      width: 15%;
    }

    .updated_at {
      width: 40%;
    }

  }
</style>
